import { Product, QuoteLineDto, QuoteProduct } from 'queries';
import {
  computeIncentive,
  getProductsByCountryGroups,
  getUnitProductPrice,
  NO_PRICE_GROUP,
  toProductArr,
} from 'lib/priceComputation';
import { getGroupProducts, getRelevantProducts } from 'lib/planAndOffer';

export interface QuoteLine {
  id: string;
  sourceIds?: string[];
  productId?: string;
  productName?: string;
  title: string;
  unitPrice: number;
  quantity: number;
  pricePerParticipant?: boolean;
  countryPriceGroupId?: string;
  edited?: boolean;
}

export const VAT_RATE = 0.2;

export const uid = () => Math.random().toString(36).slice(2, 10);

export const productTitle = (product: Product) =>
  product.title || String(product.name);

export const lineTotal = (line: QuoteLine) => line.unitPrice * line.quantity;

export const sumLines = (lines: QuoteLine[]) =>
  lines.reduce((acc, line) => acc + lineTotal(line), 0);

const buildLines = (
  quoteProducts: QuoteProduct[],
  duration: number,
  numberOfParticipants: number,
): QuoteLine[] => {
  const incentive = computeIncentive(quoteProducts, duration);

  return quoteProducts
    .map((quoteProduct) => {
      const product = quoteProduct.product;
      const unitPrice =
        quoteProduct.unitPrice ??
        (product ? getUnitProductPrice(product, duration, incentive) : 0);

      return {
        id: uid(),
        sourceIds: quoteProduct.id ? [quoteProduct.id] : [],
        productId: quoteProduct.productId,
        productName: product ? String(product.name) : undefined,
        title: quoteProduct.title || (product ? productTitle(product) : ''),
        unitPrice,
        quantity: product?.pricePerParticipant
          ? numberOfParticipants
          : quoteProduct.quantity ?? 1,
        pricePerParticipant: product?.pricePerParticipant,
        countryPriceGroupId: quoteProduct.countryPriceGroupId,
        edited:
          quoteProduct.title != null || quoteProduct.unitPrice != null
            ? true
            : undefined,
      };
    })
    .filter((line) => line.unitPrice !== 0 && line.quantity !== 0)
    .reduce(mergeSameProduct, [] as QuoteLine[]);
};

export const quoteToLines = (
  quoteProducts: QuoteProduct[] | undefined,
  duration: number,
  numberOfParticipants: number,
): QuoteLine[] => {
  const groups = Object.keys(getProductsByCountryGroups(quoteProducts)).filter(
    (group) => group !== NO_PRICE_GROUP,
  );

  const candidates = groups.length
    ? groups.map((group) => getGroupProducts(quoteProducts, group))
    : [getRelevantProducts(quoteProducts, {})];

  let best: QuoteLine[] = [];
  let maxTotal = -1;

  for (const candidate of candidates) {
    const lines = buildLines(candidate, duration, numberOfParticipants);
    const total = sumLines(lines);

    if (total > maxTotal) {
      maxTotal = total;
      best = lines;
    }
  }

  return best;
};

const mergeSameProduct = (lines: QuoteLine[], line: QuoteLine) => {
  const existing = line.productId
    ? lines.find(
        (l) =>
          l.productId === line.productId &&
          l.unitPrice === line.unitPrice &&
          l.title === line.title,
      )
    : undefined;

  if (existing) {
    existing.quantity += line.quantity;
    existing.sourceIds = [
      ...(existing.sourceIds || []),
      ...(line.sourceIds || []),
    ];
    return lines;
  }

  return [...lines, line];
};

export const linesToDto = (lines: QuoteLine[]): QuoteLineDto[] =>
  lines.map((line) => {
    const overridden = !!line.edited || !line.productId;

    return {
      productId: line.productId,
      title: overridden ? line.title : undefined,
      unitPrice: overridden ? Math.round(line.unitPrice) : undefined,
      quantity: line.pricePerParticipant
        ? 1
        : Math.max(Math.round(line.quantity), 1),
      countryPriceGroupId: line.countryPriceGroupId,
    };
  });

export const relocalizeLines = (
  lines: QuoteLine[],
  quoteProducts?: QuoteProduct[],
): QuoteLine[] => {
  const products = toProductArr(quoteProducts);

  if (!products.length) {
    return lines;
  }

  return lines.map((line) => {
    const product = products.find(
      (p) =>
        p.id === line.productId ||
        (!!line.productName && String(p.name) === line.productName),
    );

    return product?.title ? { ...line, title: product.title } : line;
  });
};

export const applyParticipantCount = (
  lines: QuoteLine[],
  numberOfParticipants: number,
): QuoteLine[] =>
  lines.map((line) =>
    line.pricePerParticipant
      ? { ...line, quantity: numberOfParticipants }
      : line,
  );
